import edgeFunctionService from './edgeFunctionService';

/**
 * App-side Meeting Autopilot trigger.
 *
 * Recordings that arrive by webhook (Recall bots, Fathom auto-sync) queue autopilot on the
 * server. Sessions added in the app (a transcript upload with "Run autopilot" ticked, or a Fathom
 * meeting imported into a project) call the `meeting-autopilot` edge function, which checks
 * access and the account's `autopilot` feature flag and keeps one job per session.
 *
 * Fire and forget: autopilot is extra, so a failure here never fails the upload or the import.
 */

/** Edge function that queues the job. */
export const MEETING_AUTOPILOT_FUNCTION = 'meeting-autopilot';

/**
 * Queue Meeting Autopilot for a session the current user just added.
 * @param {string} sessionId - Session id.
 * @param {object} [options] - Options.
 * @param {(name: string, payload: object) => Promise<object>} [options.callFunction] - Edge caller (tests).
 * @returns {Promise<{success: boolean, status: string, requestId?: string}>} Outcome; never rejects.
 */
export async function requestMeetingAutopilot(sessionId, { callFunction } = {}) {
  if (typeof sessionId !== 'string' || !sessionId) return { success: false, status: 'skipped' };
  const call = callFunction || ((name, payload) => edgeFunctionService.callFunction(name, payload));
  try {
    const response = await call(MEETING_AUTOPILOT_FUNCTION, { sessionId });
    if (!response || typeof response !== 'object') return { success: false, status: 'error' };
    return {
      success: response.success === true,
      status: typeof response.status === 'string' ? response.status : (response.success ? 'queued' : 'error'),
      ...(typeof response.requestId === 'string' ? { requestId: response.requestId } : {}),
    };
  } catch {
    // No session content in logs.
    console.warn('Meeting Autopilot could not be started for this session.');
    return { success: false, status: 'error' };
  }
}
